export interface TcExamRule {
  label: string;
  questions: number;
  timeHours: number;
  passPercent: number;
  passLabel: string;
  description: string;
}

export const TC_CPL_EXAM: TcExamRule = {
  label: "CPL Written Examination",
  questions: 100,
  timeHours: 3,
  passPercent: 70,
  passLabel: "70% (70/100)",
  description: "Mixed questions drawn from all subject areas: Air Law, Meteorology, Navigation, and General Knowledge.",
};

/** Student pilot written — typical paper format (adjust in admin copy if your bank differs). */
export const TC_PSTAR_EXAM: TcExamRule = {
  label: "PSTAR (Student Pilot)",
  questions: 50,
  timeHours: 1.5,
  passPercent: 90,
  passLabel: "90%",
  description: "Practice for the PSTAR-style written. Question count and pass mark follow common Transport Canada study materials; not an official exam paper.",
};

/** Restricted Operator Certificate (Aviation) — typical study-exam format. */
export const TC_ROCA_EXAM: TcExamRule = {
  label: "ROC-A (Radio Operator)",
  questions: 25,
  timeHours: 1,
  passPercent: 70,
  passLabel: "70%",
  description: "Practice for ROC-A style radio regulations questions. Format is for app familiarity only; not an Industry Canada examination paper.",
};

export function getExamRuleForTrack(track: string | null | undefined): TcExamRule {
  if (track === "pstar") return TC_PSTAR_EXAM;
  if (track === "roca") return TC_ROCA_EXAM;
  return TC_CPL_EXAM;
}

export interface SectionResult {
  category: string;
  correct: number;
  total: number;
  percent: number;
  passed: boolean;
}

export interface ExamResult {
  overallPercent: number;
  overallPassed: boolean;
  sections: SectionResult[];
  failedSections: SectionResult[];
  /** True only if overall ≥ 70% AND every section ≥ 70% */
  fullPass: boolean;
}

function derivedCorrect(ans: {
  isCorrect: boolean;
  selectedOptionIndex: number;
  question: { correctOptionIndex: number };
}): boolean {
  const selectedAnswer = Number(
    ans.selectedOptionIndex ?? (ans as any).selectedOption
  );

  const correctAnswer = Number(
    ans.question.correctOptionIndex ?? (ans.question as any).correctOption
  );

  if (!isNaN(selectedAnswer) && !isNaN(correctAnswer)) {
    return selectedAnswer === correctAnswer;
  }

  return Boolean(ans.isCorrect);
}

export function calcExamResult(
  answers: {
    isCorrect: boolean;
    selectedOptionIndex: number;
    question: { category: string; correctOptionIndex: number };
  }[],
  rule: TcExamRule = TC_CPL_EXAM,
): ExamResult {
  const map: Record<string, { correct: number; total: number }> = {};

  for (const ans of answers) {
    const cat = ans.question.category;
    if (!map[cat]) map[cat] = { correct: 0, total: 0 };
    map[cat].total++;
    if (derivedCorrect(ans)) map[cat].correct++;
  }

  const sections: SectionResult[] = Object.entries(map).map(([category, { correct, total }]) => {
    const percent = total > 0 ? Math.round((correct / total) * 100) : 0;
    return { category, correct, total, percent, passed: percent >= rule.passPercent };
  }).sort((a, b) => a.category.localeCompare(b.category));

  const total = answers.length;
  const correct = answers.filter(a => derivedCorrect(a)).length;
  const overallPercent = total > 0 ? Math.round((correct / total) * 100) : 0;
  const overallPassed = overallPercent >= rule.passPercent;
  const failedSections = sections.filter(s => !s.passed);
  const fullPass = overallPassed && failedSections.length === 0;

  return { overallPercent, overallPassed, sections, failedSections, fullPass };
}

export function isExamPassing(correctCount: number, totalQuestions: number, rule: TcExamRule = TC_CPL_EXAM): boolean {
  if (totalQuestions === 0) return false;
  return (correctCount / totalQuestions) * 100 >= rule.passPercent;
}
